import React, { useLayoutEffect, useRef, useEffect, useState } from "react";
import {
  Checkbox,
  Grid,
  InputOnChangeData,
  Ref,
  TextArea,
} from "semantic-ui-react";
import styled from "styled-components";
import { SelectedTextType } from "./ProductTextModalEditor";
import { preventOverflowOnTextarea } from "../products/copy-assistant/utils";
import { Button } from "../components/tailwind/button/Button";

const StyledTextArea = styled(TextArea)`
  font-family: Consolas, Monaco, Lucida Console, Liberation Mono,
    DejaVu Sans Mono, Bitstream Vera Sans Mono, Courier New, monospace;
  height: 100%;
  padding: 0.5em;
`;

const EditorContainer = styled.div`
  height: 100%;
  min-height: 500px;

  & .tox-tinymce {
    min-height: 500px !important;
  }
`;

type Props = {
  onChange: (textType: SelectedTextType, value: string) => void;
  onSelect: (textType: SelectedTextType) => void;
  selected: boolean;
  selectedTextType: SelectedTextType;
  showSelector?: boolean;
  text: string;
  title: string;
};

export const ProductTextModalEditBox: React.FC<Props> = ({
  onChange,
  onSelect,
  selected,
  selectedTextType,
  showSelector,
  text,
  title,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>();
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const [isCodeMode, setIsCodeMode] = useState(false);
  const editorId = `tinymce-${selectedTextType}`;

  useEffect(() => {
    if (isCodeMode || !editorRef.current) return;

    const initEditor = (): void => {
      if (!(window as any).tinymce) {
        setTimeout(initEditor, 100);
        return;
      }

      const tinymce = (window as any).tinymce;
      const existing = tinymce.get(editorId);
      if (existing) tinymce.remove(existing);

      const currentSelectedTextType = selectedTextType;
      const currentOnChange = onChange;

      tinymce.init({
        target: editorRef.current,
        license_key: "gpl",
        plugins: "code preview wordcount",
        toolbar:
          "undo redo | formatselect | bold italic | alignleft aligncenter alignright | code | preview",
        height: 400,
        menubar: false,
        setup: (editor: any) => {
          if (text) editor.setContent(text);
          editor.on("change", () => {
            try {
              currentOnChange(currentSelectedTextType, editor.getContent());
            } catch (error) {
              console.error("Error in TinyMCE onChange:", error);
            }
          });
          // Prevent Enter key from triggering modal button actions
          editor.on("keydown", (e: any) => {
            if (e.keyCode === 13) {
              e.stopPropagation();
            }
          });
        },
      });
    };

    initEditor();

    return () => {
      if ((window as any).tinymce) {
        const editor = (window as any).tinymce.get(editorId);
        if (editor) (window as any).tinymce.remove(editor);
      }
    };
  }, [selectedTextType, isCodeMode, editorId]);

  useEffect(() => {
    if (!isCodeMode || !editorRef.current || !(window as any).tinymce) return;

    const editor = (window as any).tinymce.get(editorId);
    if (!editor) return;

    const currentContent = editor.getContent();
    if (text && currentContent !== text) {
      try {
        editor.setContent(text);
      } catch (error) {
        console.error("Error updating TinyMCE content:", error);
      }
    }
  }, [text, isCodeMode, editorId]);

  const toggleMode = (): void => {
    if (isCodeMode) {
      setIsCodeMode(false);
    } else {
      if ((window as any).tinymce) {
        const editor = (window as any).tinymce.get(editorId);
        if (editor && editor.initialized) {
          const content = editor.getContent();
          onChange(selectedTextType, content);
        }
      }
      setIsCodeMode(true);
    }
  };

  useLayoutEffect(() => {
    if (textareaRef.current && isCodeMode) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, [textareaRef, text, isCodeMode]);

  return (
    <Grid.Column data-testid={"product-text-modal-edit-box"}>
      <h3 data-tesid={"product-text-modal-edit-box-title"}>{title}</h3>
      <div style={{ marginBottom: "10px" }}>
        <Button type="button" onClick={toggleMode} size="sm" compact>
          {isCodeMode ? "Switch to Visual" : "Switch to Code"}
        </Button>
      </div>
      {isCodeMode ? (
        <Ref
          innerRef={(el): void => {
            if (el?.ref) textareaRef.current = el.ref.current;
          }}
        >
          <StyledTextArea
            data-action={"edit-product-text"}
            data-testid={`edit-product-text-${selectedTextType}`}
            onChange={(
              e: React.ChangeEvent<TextArea>,
              { value }: InputOnChangeData
            ): void => onChange(selectedTextType, value)}
            value={text || ""}
          />
        </Ref>
      ) : (
        <EditorContainer>
          <textarea
            id={editorId}
            ref={editorRef}
            data-testid={`edit-product-text-${selectedTextType}`}
            defaultValue={text || ""}
            style={{ width: "100%", minHeight: "400px" }}
          />
        </EditorContainer>
      )}
      {showSelector && (
        <Checkbox
          data-testid={"product-text-modal-edit-box-select"}
          radio
          label={"Select this text to be saved"}
          name={"selectedText"}
          value={selectedTextType}
          checked={selected}
          onChange={(): void => onSelect(selectedTextType)}
        />
      )}
    </Grid.Column>
  );
};
ProductTextModalEditBox.defaultProps = { showSelector: true };
